import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { BiMapPin } from 'react-icons/bi';

import { API_BASE_URL } from '../utils/urls.js';

const LocationsDashboard = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchUserLocations = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Authentication token not found.');
                setLoading(false);
                return;
            }

            try {
                // Fetch all users who could have location data (field agents, case managers)
                const usersResponse = await axios.get(`${API_BASE_URL}/users/assignable`, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                const usersWithLocations = await Promise.all(
                    usersResponse.data.map(async (user) => {
                        const locationResponse = await axios.get(`${API_BASE_URL}/locations/user/${user.id}/latest`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                        return {
                            ...user,
                            latestLocation: locationResponse.data,
                        };
                    })
                );

                setUsers(usersWithLocations);
            } catch (err) {
                console.error('Error fetching user locations:', err);
                setError(err.response?.data?.message || 'Failed to load location data.');
            } finally {
                setLoading(false);
            }
        };

        fetchUserLocations();
    }, []);

    if (loading) return <div className="p-6 text-center">Loading user locations...</div>;
    if (error) return <div className="p-6 text-center text-red-500">{error}</div>;

    return (
        <div className="p-6">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">User Locations Dashboard</h1>
            <p className="mb-4 text-gray-600">Overview of the latest known locations for field agents and case managers.</p>
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Latest Location</th>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tracked At</th>
                            <th scope="col" className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {users.length > 0 ? (
                            users.map(user => (
                                <tr key={user.id} className="hover:bg-gray-50 transition-colors duration-200">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {user.first_name} {user.last_name} ({user.role_name})
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {user.latestLocation?.latitude && user.latestLocation?.longitude ? `${user.latestLocation.latitude}, ${user.latestLocation.longitude}` : 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {user.latestLocation?.tracked_at ? new Date(user.latestLocation.tracked_at).toLocaleString() : 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <Link to={`/cases`} className="text-blue-600 hover:text-blue-900">
                                            <BiMapPin className="inline-block h-5 w-5 mr-1" /> View History
                                        </Link>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">No users with location data found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default LocationsDashboard;